import { extendVariants, Button as HeroButton, ButtonProps as HeroButtonProps, Link } from '@heroui/react';
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentProps,
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { HeroBaseProps } from '../utils';
import { Spinner } from './Spinner';

/* eslint-disable sonarjs/no-duplicate-string */

// For all Button props see https://v2.heroui.com/docs/components/button
const StyledButton = extendVariants(
  forwardRef<
    HTMLButtonElement,
    HeroBaseProps<
      HeroButtonProps,
      | 'variant'
      | 'color'
      | 'size'
      | 'startContent'
      | 'endContent'
      | 'fullWidth'
      | 'isIconOnly'
      | 'isDisabled'
      | 'isLoading'
      | 'radius'
      | 'as'
    > & {
      // Limit variants and colors for simpler interface
      variant?: Extract<HeroButtonProps['variant'], 'bordered' | 'light' | 'solid' | 'flat'>;
      color?: Exclude<HeroButtonProps['color'], 'default'>;
    } & {
      /** If true, the button will have a shadow */
      hasShadow?: boolean;
    } & Pick<ComponentProps<typeof Link>, 'isExternal' | 'href' | 'showAnchorIcon'> & // Needed for when using `as={Link}`
      Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel'> & // Needed for when using `as="a"`
      Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'form'> // Needed for native submit buttons
  >((props, ref) => {
    return (
      <HeroButton
        ref={ref}
        {...props}
        className={twMerge(
          '!transform-none font-medium antialiased h-auto min-w-auto focus:ring-secondary-500/15 focus:ring',
          props.className,
        )}
        spinner={props.isLoading && <Spinner size={props.size} color={props.color} />}
      />
    );
  }),
  {
    variants: {
      size: {
        '2xs': '!rounded-md gap-1 text-xs min-h-5 [&>svg]:h-3 [&>svg]:w-3',
        xs: '!rounded-md gap-1.5 text-xs min-h-6 [&>svg]:h-3.5 [&>svg]:w-3.5',
        sm: '!rounded-md gap-2 text-sm min-h-7 [&>svg]:h-3.5 [&>svg]:w-3.5',
        md: '!rounded-lg gap-2 text-sm min-h-8 [&>svg]:h-4 [&>svg]:w-4',
        lg: '!rounded-lg gap-2 text-sm min-h-9 [&>svg]:h-4 [&>svg]:w-4',
        xl: '!rounded-lg gap-2 text-sm min-h-10 [&>svg]:h-4 [&>svg]:w-4',
      },
      variant: {
        bordered: 'border bg-transparent',
        solid: 'border-none text-white',
        flat: 'border-none',
        light: 'border-none',
      },
      color: {
        primary: '',
        secondary: '',
        warning: '',
        success: '',
        danger: '',
      },
      radius: {
        // Each `radius` value uses `!` so it wins against the `rounded-*` baked
        // into the `size` variants above. Consumers can pass any HeroUI radius.
        none: '!rounded-none',
        sm: '!rounded-sm',
        md: '!rounded-md',
        lg: '!rounded-lg',
        full: '!rounded-full',
      },
      isLoading: {
        true: '[&_[aria-label="Loading"]]:absolute !cursor-wait text-secondary-400/50 [&>svg]:text-secondary-400',
      },
      hasShadow: {
        true: '',
        false: 'shadow-none',
      },
      isIconOnly: {
        false: '[&>svg]:opacity-60',
        true: 'aspect-square',
      },
      isDisabled: {
        true: 'cursor-not-allowed',
      },
    },
    defaultVariants: {
      size: 'sm',
      variant: 'solid',
      color: 'secondary',
    },
    compoundVariants: [
      // Size combos
      {
        size: '2xs',
        variant: 'bordered',
        isIconOnly: false,
        class: 'py-px px-[5px]',
      },
      {
        size: '2xs',
        variant: ['solid', 'flat', 'light'],
        isIconOnly: false,
        class: 'py-0.5 px-1.5',
      },
      {
        size: 'xs',
        variant: 'bordered',
        isIconOnly: false,
        class: 'py-[3px] px-[7px]',
      },
      {
        size: 'xs',
        variant: ['solid', 'flat', 'light'],
        isIconOnly: false,
        class: 'py-1 px-2 ',
      },
      {
        size: 'sm',
        variant: 'bordered',
        isIconOnly: false,
        class: 'py-[3px] px-[7px]',
      },
      {
        size: 'sm',
        variant: ['solid', 'flat', 'light'],
        isIconOnly: false,
        class: 'py-1 px-2',
      },
      {
        size: 'md',
        variant: 'bordered',
        isIconOnly: false,
        class: 'py-[5px] px-[9px]',
      },
      {
        size: 'md',
        variant: ['solid', 'flat', 'light'],
        isIconOnly: false,
        class: 'py-1.5 px-2.5 ',
      },
      {
        size: 'lg',
        variant: 'bordered',
        isIconOnly: false,
        class: 'py-[7px] px-[11px]',
      },
      {
        size: 'lg',
        variant: ['solid', 'flat', 'light'],
        isIconOnly: false,
        class: 'py-2 px-3 ',
      },
      {
        size: 'xl',
        variant: 'bordered',
        isIconOnly: false,
        class: 'py-[9px] px-[13px]',
      },
      {
        size: 'xl',
        variant: ['solid', 'flat', 'light'],
        isIconOnly: false,
        class: 'py-2.5 px-3.5 ',
      },
      // Solid combos
      {
        variant: 'solid',
        color: 'primary',
        isLoading: false,
        class: 'bg-primary-600 hover:bg-primary-700',
      },
      {
        variant: 'solid',
        color: 'secondary',
        isLoading: false,
        class: 'bg-secondary-950 hover:bg-secondary-900',
      },
      {
        variant: 'solid',
        color: 'warning',
        isLoading: false,
        class: 'bg-warning-500 hover:bg-warning-600',
      },
      {
        variant: 'solid',
        color: 'success',
        isLoading: false,
        class: 'bg-success-500 hover:bg-success-600',
      },
      {
        variant: 'solid',
        color: 'danger',
        isLoading: false,
        class: 'bg-danger-500 hover:bg-danger-600',
      },
      // Flat combos
      {
        variant: 'flat',
        color: 'primary',
        isLoading: false,
        class: 'text-primary-600 bg-primary-200 hover:bg-primary-300',
      },
      {
        variant: 'flat',
        color: 'secondary',
        isLoading: false,
        class: 'text-secondary-950 bg-secondary-200 hover:bg-secondary-300',
      },
      {
        variant: 'flat',
        color: 'warning',
        isLoading: false,
        class: 'text-warning-700 bg-warning-200 hover:bg-warning-300',
      },
      {
        variant: 'flat',
        color: 'success',
        isLoading: false,
        class: 'text-success-600 bg-success-200 hover:bg-success-300',
      },
      {
        variant: 'flat',
        color: 'danger',
        isLoading: false,
        class: 'text-danger-500 bg-danger-200 hover:bg-danger-300',
      },
      // Bordered combos
      {
        variant: 'bordered',
        color: 'primary',
        isLoading: false,
        class: 'text-primary-600 border-primary-300 hover:border-primary-400 hover:bg-primary-50',
      },
      {
        variant: 'bordered',
        color: 'secondary',
        isLoading: false,
        class: 'text-secondary-950 border-secondary-300 hover:border-secondary-400 hover:bg-secondary-50',
      },
      {
        variant: 'bordered',
        color: 'warning',
        isLoading: false,
        class: 'text-warning-600 border-warning-300 hover:border-warning-400 hover:bg-warning-50',
      },
      {
        variant: 'bordered',
        color: 'success',
        isLoading: false,
        class: 'text-success-600 border-success-300 hover:border-success-400 hover:bg-success-50',
      },
      {
        variant: 'bordered',
        color: 'danger',
        isLoading: false,
        class: 'text-danger-500 border-danger-300 hover:border-danger-400 hover:bg-danger-50',
      },
      // Light combos
      {
        variant: 'light',
        color: 'primary',
        isLoading: false,
        class: 'text-primary-600 bg-transparent hover:!bg-primary-50',
      },
      {
        variant: 'light',
        color: 'secondary',
        isLoading: false,
        class: 'text-secondary-950 bg-transparent hover:!bg-secondary-100',
      },
      {
        variant: 'light',
        color: 'warning',
        isLoading: false,
        class: 'text-warning-600 bg-transparent hover:!bg-warning-50',
      },
      {
        variant: 'light',
        color: 'success',
        isLoading: false,
        class: 'text-success-600 bg-transparent hover:!bg-success-50',
      },
      {
        variant: 'light',
        color: 'danger',
        isLoading: false,
        class: 'text-danger-500 bg-transparent hover:!bg-danger-50',
      },
      // Disabled combos
      {
        variant: 'solid',
        isDisabled: true,
        class: '!bg-secondary-300 !text-secondary-400',
      },
      {
        variant: 'flat',
        isDisabled: true,
        class: '!bg-secondary-200 !text-secondary-400',
      },
      {
        variant: 'light',
        isDisabled: true,
        class: '!bg-transparent !text-secondary-400 hover:!bg-transparent',
      },
      {
        variant: 'bordered',
        isDisabled: true,
        class: '!border-secondary-300 !text-secondary-400 hover:!border-secondary-300',
      },
      // hasShadow combos
      {
        variant: ['bordered', 'light'],
        hasShadow: true,
        class: '!bg-white',
      },
      {
        size: ['2xs', 'xs', 'sm', 'md'],
        hasShadow: true,
        isLoading: false,
        isDisabled: false,
        class: 'shadow-xs',
      },
      {
        size: ['lg', 'xl'],
        hasShadow: true,
        isLoading: false,
        isDisabled: false,
        class: 'shadow-sm',
      },
      // Matches the disabled `Select` and `Input` fill. Declared after the `hasShadow` combos so it
      // wins over their white fill, which is only right for an enabled control.
      {
        variant: 'bordered',
        isDisabled: true,
        class: '!bg-secondary-50 hover:!bg-secondary-50',
      },
      // isLoading combos
      {
        isLoading: true,
        variant: 'solid',
        class: 'bg-secondary-300 hover:bg-secondary-300',
      },
      {
        isLoading: true,
        variant: 'flat',
        class: 'bg-secondary-200 hover:bg-secondary-200',
      },
      {
        isLoading: true,
        variant: 'bordered',
        class: 'border-secondary-300 hover:border-secondary-300 hover:bg-transparent',
      },
      {
        isLoading: true,
        variant: 'light',
        class: 'bg-secondary-200 hover:bg-transparent',
      },
      // Icon only combos
      {
        isIconOnly: true,
        size: '2xs',
        class: 'h-5 w-5 max-h-5 min-w-5',
      },
      {
        isIconOnly: true,
        size: 'xs',
        class: 'h-6 w-6 max-h-6 min-w-6',
      },
      {
        isIconOnly: true,
        size: 'sm',
        class: 'h-7 w-7 max-h-7 min-w-7',
      },
      {
        isIconOnly: true,
        size: 'md',
        class: 'h-8 w-8 max-h-8 min-w-8',
      },
      {
        isIconOnly: true,
        size: 'lg',
        class: 'h-9 w-9 max-h-9 min-w-9',
      },
      {
        isIconOnly: true,
        size: 'xl',
        class: 'h-10 w-10 max-h-10 min-w-10',
      },
    ],
  },
);

// `extendVariants` hardcodes its ref type to `ReactElement`; re-expose the underlying button DOM
// node so consumers can attach a typed ref (focus management, drag handles, measurements...).
export const Button = StyledButton as ForwardRefExoticComponent<
  PropsWithoutRef<ComponentProps<typeof StyledButton>> & RefAttributes<HTMLButtonElement>
>;

export type ButtonProps = Parameters<typeof Button>[0];
